import type {
  AssistantResponseSources,
  ProvenanceSearchedEntry,
  ProvenanceSource,
  SourceClassification,
} from "../../types/provenance";
import { EXTERNAL_CITE_BASE } from "./clientWebExternals";

/**
 * Live CITED-chunk overlay — the chunk-source twin of the web overlay in
 * {@link ./clientWebExternals}. Everything the funnel needs for this turn is
 * already in the browser while the answer streams: the sources metadata events
 * delivered the chunk cards (`responseSources`) and the answer text carries the
 * `<cite>` markers. The provenance payload only catches up at the post-turn
 * fetch — and after a mid-generation Stop the backend may briefly have nothing
 * at all — so without the overlay the panel shows sources the chat is visibly
 * citing only after that round-trip.
 */

/** A legacy `responseSources` entry (SourceDataRecordAndChunk over SSE). */
export interface LiveChunkSource {
  source_data_record?: {
    id?: number;
    dataset_fk?: number;
    project_folder_fk?: number;
    data?: Record<string, unknown>;
  };
  dataset?: { id: number; name: string } | null;
  data_chunk?: { id?: number };
  classification?: SourceClassification | null;
  retrieved_by_agent?: string | null;
  retrieval_events?: unknown[];
  primary_retrieval?: unknown;
  retrieved_at?: string | null;
  retrieval_query?: string | null;
  revision?: string | null;
}

// The model emits `<cite>N</cite>`; process_citations may rewrite it mid-stream
// to the resolved `<cite>{…}</cite>` form. Cover both (mirror of the backend's
// extract_cited_chunk_ids_from_text and the web overlay's regexes).
const CITE_NUMERIC = /<cite>(\d+)<\/cite>/g;
const CITE_JSON = /<cite>(\{.*?\})<\/cite>/g;

/**
 * Chunk ids cited in `content`, in first-seen order, deduped. Restricted to
 * the chunk band (< {@link EXTERNAL_CITE_BASE}) — external cite ids belong to
 * the web overlay. JSON payloads carrying an `error` key are skipped: a
 * failed-resolution card is not a citation (same rule as the backend).
 */
export function citedChunkIdsInOrder(content: string): number[] {
  const found: Array<[number, number]> = []; // [position, chunk_id]
  let m: RegExpExecArray | null;
  CITE_NUMERIC.lastIndex = 0;
  while ((m = CITE_NUMERIC.exec(content)) !== null) {
    const id = Number(m[1]);
    if (id < EXTERNAL_CITE_BASE) found.push([m.index, id]);
  }
  CITE_JSON.lastIndex = 0;
  while ((m = CITE_JSON.exec(content)) !== null) {
    try {
      const payload = JSON.parse(m[1]) as {
        chunk_id?: unknown;
        error?: unknown;
      };
      if (payload && typeof payload === "object" && "error" in payload) {
        continue;
      }
      const id = payload?.chunk_id;
      if (typeof id === "number" && id < EXTERNAL_CITE_BASE) {
        found.push([m.index, id]);
      }
    } catch {
      // malformed payload — ignore
    }
  }
  found.sort((a, b) => a[0] - b[0]);
  const seen = new Set<number>();
  const out: number[] = [];
  for (const [, id] of found) {
    if (!seen.has(id)) {
      seen.add(id);
      out.push(id);
    }
  }
  return out;
}

/** Project a live legacy source into the funnel's ProvenanceSource shape. */
function toProvenanceSource(
  live: LiveChunkSource,
  cited: boolean,
): ProvenanceSource {
  const sdr = live.source_data_record;
  const classification: SourceClassification =
    live.classification ??
    (sdr?.project_folder_fk != null ? "project_file" : "org_dataset");
  return {
    source_data_record: sdr,
    // Project-folder chunks have no backing dataset — keep the card renderable.
    dataset: live.dataset ?? { id: sdr?.dataset_fk ?? -1, name: "" },
    data_chunk: live.data_chunk,
    classification,
    retrieved_by_agent: live.retrieved_by_agent ?? null,
    cited,
    retrieval_events: live.retrieval_events,
    primary_retrieval: live.primary_retrieval ?? null,
    retrieved_at: live.retrieved_at ?? null,
    retrieval_query: live.retrieval_query ?? null,
    revision: live.revision ?? null,
  } as ProvenanceSource;
}

/**
 * Fold LIVE chunk sources into a provenance payload for the Sources panel.
 * The backend payload stays authoritative — live entries are only appended
 * for chunk ids it doesn't own yet (in either tier): cited ids join the Cited
 * tier in cite order, the remaining live sources join Read (they entered the
 * model's context this turn). Returns the payload identity when there is
 * nothing to add. A cited id with no matching live card is skipped rather
 * than invented — the post-turn fetch resolves it. Adding sources clears the
 * `no_documents` degradation, which describes a payload we now know is stale.
 */
export function overlayLiveCitedChunks(
  provenance: AssistantResponseSources,
  liveSources: LiveChunkSource[] | undefined,
  citedIdsInOrder: number[],
): AssistantResponseSources {
  if (!liveSources?.length) return provenance;
  const owned = new Set(
    [
      ...(provenance.assistant_response_sources ?? []),
      ...(provenance.read_sources ?? []),
    ]
      .map((s) => s.data_chunk?.id)
      .filter((id): id is number => id != null),
  );
  const freshById = new Map<number, LiveChunkSource>();
  for (const live of liveSources) {
    const id = live?.data_chunk?.id;
    if (id == null || owned.has(id) || freshById.has(id)) continue;
    freshById.set(id, live);
  }
  if (freshById.size === 0) return provenance;

  const citedAdditions: ProvenanceSource[] = [];
  for (const id of citedIdsInOrder) {
    const live = freshById.get(id);
    if (!live) continue;
    freshById.delete(id);
    citedAdditions.push(toProvenanceSource(live, true));
  }
  const readAdditions = [...freshById.values()].map((live) =>
    toProvenanceSource(live, false),
  );

  return {
    ...provenance,
    assistant_response_sources: [
      ...(provenance.assistant_response_sources ?? []),
      ...citedAdditions,
    ],
    read_sources: [...(provenance.read_sources ?? []), ...readAdditions],
    degradation: provenance.degradation
      ? { ...provenance.degradation, no_documents: null }
      : provenance.degradation,
  };
}

/**
 * Identity of a Searched row across the live stream and the settled payload:
 * stamp+query+tool when timestamped, else query+tool+result count.
 */
const searchedKey = (e: ProvenanceSearchedEntry): string =>
  e.timestamp != null
    ? `t|${e.timestamp}|${e.query_text ?? ""}|${e.tool_name ?? ""}`
    : `q|${e.query_text ?? ""}|${e.tool_name ?? ""}|${e.result_count ?? ""}`;

/**
 * Fold live Searched-tier entries into a provenance payload. The backend
 * payload stays authoritative — only entries it doesn't own are appended, and
 * payload identity is returned when there is nothing to add.
 */
export function overlayLiveSearched(
  provenance: AssistantResponseSources,
  liveEntries: ProvenanceSearchedEntry[] | undefined,
): AssistantResponseSources {
  if (!liveEntries?.length) return provenance;
  const owned = new Set((provenance.searched ?? []).map(searchedKey));
  const additions: ProvenanceSearchedEntry[] = [];
  for (const entry of liveEntries) {
    const key = searchedKey(entry);
    if (owned.has(key)) continue;
    owned.add(key);
    additions.push(entry);
  }
  if (!additions.length) return provenance;
  return {
    ...provenance,
    searched: [...(provenance.searched ?? []), ...additions],
  };
}
